import React, { useState, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Camera, Upload, X, Image, FileText, File, Trash2, RotateCcw,
  ZoomIn, GripVertical, Check
} from 'lucide-react';

const PhotoUploader = ({ 
  photos = [], 
  onPhotosChange, 
  maxPhotos = 10,
  acceptedTypes = ['image/*', 'application/pdf'],
  showCamera = true,
  label = "Photos & Documents"
}) => {
  const [uploading, setUploading] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  // Start camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraActive(true);
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Unable to access camera. Please check permissions.');
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  // Capture photo from camera
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    
    const newPhoto = {
      id: `photo_${Date.now()}`,
      url: dataUrl,
      type: 'image/jpeg',
      name: `Camera_${new Date().toISOString().slice(0,19).replace(/:/g,'-')}.jpg`,
      source: 'camera',
      uploadedAt: new Date().toISOString()
    };
    
    onPhotosChange([...photos, newPhoto]);
    toast.success('Photo captured!');
    stopCamera();
  };

  // Handle file drop/select
  const onDrop = useCallback(async (acceptedFiles) => {
    if (photos.length + acceptedFiles.length > maxPhotos) {
      toast.error(`Maximum ${maxPhotos} files allowed`);
      return;
    }

    setUploading(true);
    const newPhotos = [];

    for (const file of acceptedFiles) {
      try {
        // Convert to base64
        const base64 = await fileToBase64(file);
        
        newPhotos.push({
          id: `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          url: base64,
          type: file.type,
          name: file.name,
          size: file.size,
          source: 'upload',
          uploadedAt: new Date().toISOString()
        });
      } catch (error) {
        console.error('File processing error:', error);
        toast.error(`Failed to process ${file.name}`);
      }
    }

    onPhotosChange([...photos, ...newPhotos]);
    setUploading(false);
    
    if (newPhotos.length > 0) {
      toast.success(`${newPhotos.length} file(s) added!`);
    }
  }, [photos, maxPhotos, onPhotosChange]);

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Remove photo
  const removePhoto = (photoId) => {
    onPhotosChange(photos.filter(p => p.id !== photoId));
    toast.success('File removed');
  };

  // Dropzone config
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.heic'],
      'application/pdf': ['.pdf']
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    disabled: uploading || cameraActive
  });

  // Get file icon based on type
  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return <Image className="w-4 h-4" />;
    if (type === 'application/pdf') return <FileText className="w-4 h-4" />;
    return <File className="w-4 h-4" />;
  };

  // Format file size
  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">{label}</label>
        <Badge variant="outline">{photos.length}/{maxPhotos}</Badge>
      </div>

      {/* Camera View */}
      {cameraActive && (
        <Card className="overflow-hidden">
          <CardContent className="p-0 relative">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full h-64 object-cover bg-black"
            />
            <canvas ref={canvasRef} className="hidden" />
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
              <Button
                onClick={capturePhoto}
                size="lg"
                className="rounded-full w-16 h-16 bg-white hover:bg-gray-100"
              >
                <Camera className="w-8 h-8 text-black" />
              </Button>
              <Button
                onClick={stopCamera}
                size="sm"
                variant="destructive"
                className="rounded-full"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Area */}
      {!cameraActive && (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${isDragActive ? 'border-primary bg-primary/5' : 'border-stone-300 hover:border-primary'}
            ${uploading ? 'opacity-50 cursor-wait' : ''}`}
        >
          <input {...getInputProps()} />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) {
                onDrop(Array.from(e.target.files));
              }
            }}
          />
          
          <div className="flex flex-col items-center gap-3">
            <div className="flex gap-2">
              <Upload className="w-8 h-8 text-stone-400" />
            </div>
            
            {isDragActive ? (
              <p className="text-primary font-medium">Drop files here...</p>
            ) : (
              <>
                <p className="text-stone-600">
                  <span className="font-medium text-primary">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-stone-500">
                  JPEG, PNG, GIF, PDF up to 10MB
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {!cameraActive && showCamera && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={startCamera}
            className="flex-1"
            disabled={photos.length >= maxPhotos}
          >
            <Camera className="w-4 h-4 mr-2" />
            Open Camera
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="flex-1"
            disabled={photos.length >= maxPhotos}
          >
            <Image className="w-4 h-4 mr-2" />
            Take Photo
          </Button>
        </div>
      )}

      {/* Photo Grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photos.map((photo, index) => (
            <Card key={photo.id} className="relative group overflow-hidden">
              <CardContent className="p-0">
                {photo.type?.startsWith('image/') ? (
                  <div className="aspect-square relative">
                    <img
                      src={photo.url}
                      alt={photo.name}
                      className="w-full h-full object-cover"
                      onClick={() => setPreviewPhoto(photo)}
                    />
                    {index === 0 && (
                      <Badge className="absolute top-1 left-1 text-xs bg-primary">
                        Main
                      </Badge>
                    )}
                  </div>
                ) : (
                  <div 
                    className="aspect-square bg-stone-100 flex flex-col items-center justify-center p-2 cursor-pointer"
                    onClick={() => window.open(photo.url, '_blank')}
                  >
                    <FileText className="w-10 h-10 text-red-500 mb-2" />
                    <p className="text-xs text-stone-600 truncate w-full text-center">{photo.name}</p>
                  </div>
                )}
                
                {/* Overlay actions */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  {photo.type?.startsWith('image/') && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setPreviewPhoto(photo)}
                    >
                      <ZoomIn className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => removePhoto(photo.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* File info */}
                <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1 truncate">
                  {photo.source === 'camera' ? '📷' : '📁'} {formatSize(photo.size)}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Photo Preview Modal */}
      {previewPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewPhoto(null)}
        >
          <Button
            variant="ghost"
            className="absolute top-4 right-4 text-white"
            onClick={() => setPreviewPhoto(null)}
          >
            <X className="w-6 h-6" />
          </Button>
          <img
            src={previewPhoto.url}
            alt={previewPhoto.name}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {uploading && (
        <div className="flex items-center justify-center gap-2 text-sm text-stone-500">
          <RotateCcw className="w-4 h-4 animate-spin" />
          Processing files...
        </div>
      )}
    </div>
  );
};

export default PhotoUploader;
